import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLocation } from "react-router-dom";
import axios from "../utils/axios";
import toast from "react-hot-toast";
import UniversalDashboard from "../components/UniversalDashboard";
import usePagination from "../hooks/usePagination";
import Pagination from "../components/Pagination";

const OrdersDashboard = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    processing: 0,
    shipped: 0,
    delivered: 0,
    totalRevenue: 0,
  });
  const [statusFilter, setStatusFilter] = useState("all");

  // Use pagination hook
  const {
    data: orders,
    pagination,
    loading,
    error,
    setPage,
    setLimit,
    setSearch,
    setFilters,
    refresh,
    searchValue,
  } = usePagination("/api/orders", {
    initialLimit: 20,
    defaultSortBy: "created_at",
    defaultSortOrder: "DESC",
  });

  // Update filters when status changes
  useEffect(() => {
    const filters = {};
    if (statusFilter && statusFilter !== "all") {
      filters.status = statusFilter;
    }
    setFilters(filters);
  }, [statusFilter, setFilters]);

  const fetchStats = async () => {
    try {
      // Add timestamp to prevent caching
      const response = await axios.get("/api/orders", {
        params: { stats: true, _t: Date.now() },
      });
      setStats(response.data.stats || stats);
    } catch (error) {
      console.error("Error fetching order stats:", error);
    }
  };

  // Fetch stats when navigating to this page
  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // Re-fetch when pathname changes

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`/api/orders/${orderId}`, { status: newStatus });
      toast.success("Order status updated successfully");
      refresh();
      fetchStats();
    } catch (error) {
      console.error("Error updating order:", error);
      toast.error("Failed to update order status");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "processing":
        return "bg-blue-100 text-blue-800";
      case "shipped":
        return "bg-purple-100 text-purple-800";
      case "delivered":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Orders Overview Component
  const OrdersOverview = () => {
    return (
      <div>
        {/* Filter Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {[
                "all",
                "pending",
                "processing",
                "shipped",
                "delivered",
                "cancelled",
              ].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                    statusFilter === status
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {status} (
                  {status === "all" ? stats.total : stats[status] || 0})
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Orders{" "}
              {statusFilter !== "all" &&
                `- ${
                  statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)
                }`}
            </h3>

            {loading ? (
              <div className="flex justify-center py-8">
                <div className="loading-dots">
                  <div></div>
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-8 text-red-500">
                Error: {error}
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No orders found for the selected filter.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Order ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Payment
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {orders.length > 0 ? (
                        orders.map((order) => (
                          <tr key={order.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              #{order.id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {order.customer_name || "N/A"}
                              </div>
                              <div className="text-sm text-gray-500">
                                {order.customer_email || "N/A"}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              ₹{order.total_amount}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <select
                                value={order.status}
                                onChange={(e) =>
                                  updateOrderStatus(order.id, e.target.value)
                                }
                                className="text-sm border border-gray-300 rounded px-2 py-1"
                              >
                                <option value="pending">Pending</option>
                                <option value="processing">Processing</option>
                                <option value="shipped">Shipped</option>
                                <option value="delivered">Delivered</option>
                                <option value="cancelled">Cancelled</option>
                              </select>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  order.payment_status === "paid"
                                    ? "bg-green-100 text-green-800"
                                    : order.payment_status === "pending"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {order.payment_status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(order.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <button className="text-indigo-600 hover:text-indigo-900 mr-3">
                                View
                              </button>
                              <button className="text-green-600 hover:text-green-900">
                                Print
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan="7"
                            className="px-6 py-4 text-center text-sm text-gray-500"
                          >
                            No orders found for the selected filter
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Component */}
                <div className="px-6 py-4 border-t border-gray-200">
                  <Pagination
                    pagination={pagination}
                    onPageChange={setPage}
                    onLimitChange={setLimit}
                    onSearchChange={setSearch}
                    searchValue={searchValue}
                    searchPlaceholder="Search by order ID, customer name, or email..."
                    showSearch={true}
                    showLimitSelector={true}
                    limitOptions={[10, 20, 50, 100]}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Stats Cards Component
  const statsCards = (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
      <div className="bg-white rounded-lg shadow p-4">
        <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
        <div className="text-sm text-gray-600">Total Orders</div>
      </div>
      <div className="bg-white rounded-lg shadow p-4">
        <div className="text-2xl font-bold text-yellow-600">
          {stats.pending}
        </div>
        <div className="text-sm text-gray-600">Pending</div>
      </div>
      <div className="bg-white rounded-lg shadow p-4">
        <div className="text-2xl font-bold text-blue-600">
          {stats.processing}
        </div>
        <div className="text-sm text-gray-600">Processing</div>
      </div>
      <div className="bg-white rounded-lg shadow p-4">
        <div className="text-2xl font-bold text-purple-600">
          {stats.shipped}
        </div>
        <div className="text-sm text-gray-600">Shipped</div>
      </div>
      <div className="bg-white rounded-lg shadow p-4">
        <div className="text-2xl font-bold text-green-600">
          {stats.delivered}
        </div>
        <div className="text-sm text-gray-600">Delivered</div>
      </div>
      <div className="bg-white rounded-lg shadow p-4">
        <div className="text-2xl font-bold text-green-700">
          ₹{stats.totalRevenue}
        </div>
        <div className="text-sm text-gray-600">Revenue</div>
      </div>
    </div>
  );

  return (
    <UniversalDashboard
      dashboardType="orders"
      title="Orders Dashboard"
      description={`Welcome back, ${user?.name}! Manage customer orders and fulfillment.`}
      defaultComponent={OrdersOverview}
      statsCards={statsCards}
    />
  );
};

export default OrdersDashboard;
